import { useCallback, useEffect, useRef } from 'react';
import { pick } from 'ramda';
import { useLogout } from 'js/hooks/auth';
import { useDispatch } from 'react-redux';
import { SESSION_REDUCER } from 'js/store/constants';
import { useAppSelector, useCustomSelector, useUserSelector } from 'js/store/hooks';
import { setSessionDurationAction, setSessionTimeoutShownAction } from './store/actions';
import { showTimeIsFlyingPopupAction } from 'js/components/popups/TimeIsFlying';
import { showCommonPopupAction } from 'js/components/common/popups/CommonPopup';
import uuid from 'uuid-random';
import { useTranslations } from 'js/hooks/i18n';
import { log } from 'log';

const sessionTimeoutPopupId = uuid();

export const useSession = () => {
    const timeIsFlyingTimer = useRef(-1);
    const sessionTimeoutTimer = useRef(-1);
    const sessionLogoutTimer = useRef(-1);
    const t = useTranslations();
    const logout = useLogout();

    const {
        timeFlyingPeriod,
        sessionTimerWebContent: {
            timeIsFlying: timeIsFlyingContent,
            sessionTimeout: sessionTimeoutContent
        } = {},
        logoutAfterSessionEnd
    } = useAppSelector(pick(['timeFlyingPeriod', 'sessionTimerWebContent', 'logoutAfterSessionEnd']));
    const { isLoggedIn, sessionStartTime } = useUserSelector(pick(['isLoggedIn', 'sessionStartTime']));
    const { enabled, sessionLimit, sessionTimoutShown } = useCustomSelector(SESSION_REDUCER, pick(['enabled', 'sessionLimit', 'sessionTimoutShown']));
    const dispatch = useDispatch();

    const clearTimers = () => {
        window.clearTimeout(timeIsFlyingTimer.current);
        window.clearTimeout(sessionTimeoutTimer.current);
        window.clearTimeout(sessionLogoutTimer.current);
    };

    /**
     * Should show message every `timeFlyingPeriod` minutes
     */
    const startTimeIsFlyingNotification = useCallback(
        () => {
            const periodInMs = timeFlyingPeriod * 60 * 1000;
            let counter = Math.floor((Date.now() - sessionStartTime) / periodInMs);
            const expirationTime = periodInMs - (Date.now() - sessionStartTime) % periodInMs;

            const showNotification = () => {
                dispatch(setSessionDurationAction(counter += 1));

                if (enabled && timeIsFlyingContent && timeIsFlyingContent.length > 0) {
                    dispatch(showTimeIsFlyingPopupAction());
                }

                timeIsFlyingTimer.current = window.setTimeout(showNotification, periodInMs);
            };

            timeIsFlyingTimer.current = window.setTimeout(showNotification, expirationTime);
        },
        [enabled, dispatch, sessionStartTime, timeFlyingPeriod, timeIsFlyingContent]);

    /**
     * Should show message 5 minutes before session timeout
     */
    const startSessionTimeoutTimer = useCallback(() => {
        const limitInMs = (sessionLimit - 5) * 60 * 1000;
        const expirationTime = limitInMs - (Date.now() - sessionStartTime);

        const showSessionTimeoutNotification = () => {
            if (!sessionTimoutShown && sessionTimeoutContent && sessionTimeoutContent.length > 0) {
                dispatch(showCommonPopupAction({
                    id: sessionTimeoutPopupId,
                    title: t('playtech.mobile.system.sessionTimerPopupTitle'),
                    content: sessionTimeoutContent
                }));
            }

            dispatch(setSessionTimeoutShownAction(true));
        };

        sessionTimeoutTimer.current = window.setTimeout(showSessionTimeoutNotification, expirationTime);
    }, [dispatch, sessionLimit, sessionStartTime, sessionTimeoutContent, sessionTimoutShown, t]);

    /**
     * Should logout user after `sessionLimit` minutes
     */
    const startSessionLogoutTimer = useCallback(() => {
        const limitInMs = sessionLimit * 60 * 1000;
        const expirationTime = limitInMs - (Date.now() - sessionStartTime);

        sessionLogoutTimer.current = window.setTimeout(logout, expirationTime);

        log('info', `Session expires after ${Math.ceil(expirationTime / 60000)} minutes`);
    }, [logout, sessionLimit, sessionStartTime]);

    useEffect(() => {
            if (isLoggedIn) {
                clearTimers();

                startTimeIsFlyingNotification();

                if (sessionLimit) {
                    startSessionTimeoutTimer();

                    if (logoutAfterSessionEnd) {
                        startSessionLogoutTimer();
                    }
                }
            } else {
                clearTimers();
            }
        },
        [
            isLoggedIn,
            logoutAfterSessionEnd,
            sessionLimit,
            startSessionLogoutTimer,
            startSessionTimeoutTimer,
            startTimeIsFlyingNotification
        ]);
};
